import React, { useState, useRef } from 'react';
import { Eye, Building, User, Mail, Phone, Globe, FileText, Target, Handshake, Calendar, Hash, DollarSign, Download, Check, X, ExternalLink, File } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { abvigAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel, truncateText } from '../../utils/helpers';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { abvigAdminSupabase } from '../../services/supabaseClient';
import { sendEmail } from '../../services/emailService';

export default function Partnerships() {
  const { data, refetch } = useSupabase(abvigAPI.getPartnershipInquiries);
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const contentRef = useRef();

  const handleView = (item) => {
    setSelectedPartnership(item);
    setIsModalOpen(true);
    setShowEmailForm(false);
  };

  const handleSendEmail = async (status) => {
    if (!selectedPartnership) {
      toast.error('No partnership selected');
      return;
    }
    
    setIsProcessing(true);
    setIsSendingEmail(true);
    
    try {
      const partnershipId = selectedPartnership.id;
      console.log(`📧 Processing ${status} for partnership ID:`, partnershipId);
      
      // Step 1: Update status in database
      const { data: updateData, error: updateError } = await abvigAdminSupabase
        .from('partnership_inquiries')
        .update({ status: status })
        .eq('id', partnershipId)
        .select();
      
      if (updateError) {
        console.error('❌ Supabase error:', updateError);
        toast.error(`Update failed: ${updateError.message}`);
        throw updateError;
      }
      
      console.log('✅ Update result:', updateData);
      
      if (!updateData || updateData.length === 0) {
        toast.error('Record not found');
        throw new Error('Record not found');
      }
      
      // Step 2: Refresh data
      await refetch();
      
      // Step 3: Send actual email using EmailJS
      try {
        await sendEmail({
          to: selectedPartnership.email,
          subject: emailSubject,
          message: emailMessage,
          fromName: process.env.REACT_APP_SENDER_NAME || 'AICIS Conference Team',
        });
        toast.success(`✅ Email sent to ${selectedPartnership.email}`);
      } catch (emailError) {
        console.error('❌ Email error:', emailError);
        toast.error(`Email failed to send: ${emailError.message}`);
        // Status was updated, but email failed
      }
      
      // Step 4: Close modal and reset
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
      setIsModalOpen(false);
      setSelectedPartnership(null);
      
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error(`Failed: ${error.message}`);
    } finally {
      setIsSendingEmail(false);
      setIsProcessing(false);
    }
  };

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    
    try {
      toast.loading('Generating PDF...');
      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`partnership-${selectedPartnership?.organization_name || 'inquiry'}.pdf`);
      toast.dismiss();
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
  };

  const renderDocumentPreview = (url) => {
    if (!url) return null;
    
    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
    const isPDF = /\.pdf$/i.test(url);
    
    if (isImage) {
      return (
        <div className="mt-2">
          <img 
            src={url} 
            alt="Document" 
            className="max-h-48 rounded-lg border border-gray-200 object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '';
              e.target.alt = 'Failed to load image';
            }}
          />
        </div>
      );
    } else if (isPDF) {
      return (
        <div className="mt-2 flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <File className="w-8 h-8 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">PDF Document</p>
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
            >
              View PDF <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      );
    } else {
      return (
        <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <File className="w-8 h-8 text-gray-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Document</p>
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
            >
              View Document <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      );
    }
  };

  const renderPartnershipDetails = (partnership) => {
    if (!partnership) return null;

    if (showEmailForm) {
      return (
        <div className="space-y-4" ref={contentRef}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Compose Email</h4>
            <button
              onClick={() => setShowEmailForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={isProcessing}
            >
              Back to Details
            </button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">From:</p>
            <p className="text-sm font-medium text-gray-900">{process.env.REACT_APP_SENDER_NAME || 'AICIS Conference Team'} &lt;{process.env.REACT_APP_SENDER_EMAIL || 'your-email@example.com'}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-gray-900">{partnership.email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email subject"
              disabled={isProcessing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows="6"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Type your message here..."
              disabled={isProcessing}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={() => handleSendEmail('approved')}
              disabled={isSendingEmail || !emailSubject || !emailMessage || isProcessing}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Approve & Send Email'}
            </button>
            <button
              onClick={() => handleSendEmail('rejected')}
              disabled={isSendingEmail || !emailSubject || !emailMessage || isProcessing}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Reject & Send Email'}
            </button>
            <button
              onClick={() => setShowEmailForm(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isProcessing}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div ref={contentRef}>
        <div className="space-y-6">
          {/* Organization & Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Organization Name</p>
                <p className="text-sm font-medium text-gray-900">{partnership.organization_name || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Legal Status</p>
                <p className="text-sm font-medium text-gray-900">{partnership.legal_status || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Core Services</p>
                <p className="text-sm font-medium text-gray-900">{partnership.core_services || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Headquarters</p>
                <p className="text-sm font-medium text-gray-900">{partnership.headquarters || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Country</p>
                <p className="text-sm font-medium text-gray-900">{partnership.country || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Organization Type</p>
                <p className="text-sm font-medium text-gray-900">{partnership.organization_type || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Partnership Details */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Handshake className="w-4 h-4" />
              Partnership Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Handshake className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Partnership Type</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.partnership_type || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Target className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Objectives</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.objectives || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg col-span-2">
                <Target className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Areas of Collaboration</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.areas_of_collaboration || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contribution Details */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Contribution Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Contribution Type</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.contribution_type || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Contribution Details</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.contribution_details || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg col-span-2">
                <Target className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Expected Benefits</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.expected_benefits || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Person */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Contact Person
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Contact Person</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.contact_person || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Position</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.position || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.email || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{partnership.phone || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Website</p>
                  {partnership.website ? (
                    <a 
                      href={partnership.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {partnership.website}
                    </a>
                  ) : (
                    <p className="text-sm font-medium text-gray-900">N/A</p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Submitted</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(partnership.created_at)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Documents */}
          {partnership.document_urls && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documents
              </h4>
              {renderDocumentPreview(partnership.document_urls)}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
            {/* Export PDF Button */}
            <button
              onClick={handleExportPDF}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export as PDF
            </button>

            {/* Approve/Reject with Email - Only show for pending status */}
            {partnership.status === 'pending' && (
              <>
                <button
                  onClick={() => {
                    setEmailSubject(`Partnership Inquiry Approved - ${partnership.organization_name}`);
                    setEmailMessage(`Dear ${partnership.contact_person || 'Sir/Madam'},

We are pleased to inform you that your partnership inquiry has been approved. We look forward to working with ${partnership.organization_name || 'your organization'}.

Our team will reach out to you shortly with next steps.

Best regards,
AICIS Conference Team`);
                    setShowEmailForm(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Approve & Send Email
                </button>
                <button
                  onClick={() => {
                    setEmailSubject(`Partnership Inquiry Update - ${partnership.organization_name}`);
                    setEmailMessage(`Dear ${partnership.contact_person || 'Sir/Madam'},

Thank you for your interest in partnering with us. After careful review, we regret to inform you that we are unable to proceed with this partnership at this time.

We appreciate your interest and wish you all the best.

Best regards,
AICIS Conference Team`);
                    setShowEmailForm(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Reject & Send Email
                </button>
              </>
            )}

            {/* Show current status if not pending */}
            {partnership.status !== 'pending' && (
              <div className="pt-2">
                <p className="text-sm text-gray-500 text-center">
                  This partnership has been <span className={`font-semibold ${partnership.status === 'approved' ? 'text-green-600' : 'text-red-600'}`}>
                    {getStatusLabel(partnership.status)}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'organization_name', label: 'Organization', sortable: true },
    { key: 'contact_person', label: 'Contact Person' },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'country', label: 'Country' },
    { key: 'partnership_type', label: 'Partnership Type' },
    { 
      key: 'objectives', 
      label: 'Objectives',
      render: (value) => truncateText(value, 30)
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Submitted',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'approved', 'rejected']
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Partnership Inquiries</h1>
        <p className="text-gray-600">View and manage partnership requests</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} inquiries`}
        actions={actions}
        searchPlaceholder="Search partnerships..."
        onRowClick={(item) => handleView(item)}
      />

      {/* Partnership Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPartnership(null);
          setShowEmailForm(false);
        }}
        title={showEmailForm ? "Compose Email" : "Partnership Inquiry Details"}
        size="max-w-3xl"
      >
        {renderPartnershipDetails(selectedPartnership)}
      </Modal>
    </div>
  );
}